/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import Teachr from "../../../Objects/models/Teachr";
import SessionDescriptionRepository, { SESSION_DESCRIPTION_MAX_LENGTH } from "../../../Repositories/SessionDescriptionRepository";
import { getActiveBrandConfig } from "../../../config/environment.config";
import { sessionDescriptionOverrideError } from "../../../Utils/sessionDescriptionOverride";
import { DV2 } from "../theme";
import { vocab } from "../vocabulary";
import { Card, FieldError, Hint, SectionTitle, TextLink, fieldStyle, useDv2Theme } from "../layout/Ui";

interface Props {
    teachr: Teachr | undefined;
    /** Surcharge pour ce cours (null : description habituelle). */
    value: string | null;
    onChange: (value: string | null) => void;
    /** Description habituelle chargée, remontée pour la normalisation à l'envoi. */
    onGenericLoaded?: (generic: string | null) => void;
    error?: string;
}

/**
 * « Déroulement du cours » de l'étape 1 (maquettes 1a et 2a) : rappelle la
 * description habituelle (celle des devis) et permet de la remplacer pour ce
 * cours seulement. Même logique que SessionDescriptionCard de l'ancien parcours.
 */
const SessionDescriptionPanel = ({ teachr, value, onChange, onGenericLoaded, error }: Props) => {
    const { fontFamily } = useDv2Theme();
    const navigate = useNavigate();
    const location = useLocation();
    const [generic, setGeneric] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [customizing, setCustomizing] = useState(value != null && value !== "");

    useEffect(() => {
        let cancelled = false;
        if (!teachr?.id) {
            setLoading(false);
            return undefined;
        }
        SessionDescriptionRepository.get(teachr.id)
            .then((response) => {
                if (cancelled) return;
                const text = response?.sessionDescription?.description ?? null;
                setGeneric(text);
                onGenericLoaded && onGenericLoaded(text);
            })
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teachr?.id]);

    const goToSettings = () => navigate(`/${getActiveBrandConfig()}/tableau-de-bord/parametre`, { state: { step: 5, returnTo: location.pathname } });
    const startCustomizing = () => {
        setCustomizing(true);
        if (value == null || value === "") onChange(generic ?? "");
    };
    const resetToGeneric = () => {
        setCustomizing(false);
        onChange(null);
    };
    const localError = error || sessionDescriptionOverrideError(value);
    const length = (value ?? "").trim().length;

    return (
        <Card style={{ display: "flex", flexDirection: "column", gap: 10, fontFamily }} data-testid="session-description-panel">
            <SectionTitle
                right={!loading && generic ? (
                    customizing
                        ? <TextLink label="Reprendre ma description habituelle" onClick={resetToGeneric} />
                        : <TextLink label={`Personnaliser pour ${vocab.thisCourse()}`} onClick={startCustomizing} />
                ) : null}
            >
                Déroulement {vocab.ofTheCourse()}
            </SectionTitle>

            {loading && (
                <div style={{ fontSize: 13, color: DV2.muted, display: "flex", alignItems: "center", gap: 8 }}>
                    <CircularProgress size={14} /> Chargement de votre description…
                </div>
            )}

            {!loading && !generic && (
                <>
                    <div style={{ fontSize: 14, color: DV2.textSoft, lineHeight: 1.5 }}>
                        Vous n'avez pas encore décrit le déroulement habituel de vos {vocab.courses()}. Cette description est obligatoire pour déclarer et figure sur vos devis.
                    </div>
                    <div><TextLink label="Renseigner ma description" onClick={goToSettings} /></div>
                </>
            )}

            {!loading && generic && !customizing && (
                <>
                    <div style={{ fontSize: 14, color: DV2.textSoft, lineHeight: 1.5, whiteSpace: "pre-line" }}>{generic}</div>
                    <Hint>Votre description habituelle, reprise sur le devis et visible par {vocab.theClient()}. <TextLink label="La modifier" onClick={goToSettings} size={12} /></Hint>
                </>
            )}

            {!loading && customizing && (
                <>
                    <textarea
                        value={value ?? ""}
                        onChange={(e) => onChange(e.target.value)}
                        rows={4}
                        aria-label={`Déroulement propre à ${vocab.thisCourse()}`}
                        aria-invalid={!!localError}
                        placeholder={`Décrivez ce qui sera fait pendant ${vocab.thisCourse()} en particulier`}
                        style={{ ...fieldStyle(!!localError), fontWeight: 500, fontSize: 14, lineHeight: 1.5, resize: "vertical" }}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                        {localError ? <FieldError>{localError}</FieldError> : <Hint>Remplace votre description habituelle sur le devis de {vocab.thisCourse()} uniquement.</Hint>}
                        <span style={{ fontSize: 12, color: length > SESSION_DESCRIPTION_MAX_LENGTH ? DV2.danger : DV2.placeholder, whiteSpace: "nowrap" }}>{length}/{SESSION_DESCRIPTION_MAX_LENGTH}</span>
                    </div>
                </>
            )}
        </Card>
    );
};

export default SessionDescriptionPanel;
